import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid as Grid2,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
} from "@mui/material";
import Breadcrumb from "../../../../Components/BreadCrumb";
import dayjs, { Dayjs } from "dayjs";
import { styled } from "@mui/material/styles";
import { FaRegFileExcel } from "react-icons/fa6";
import ITopupDateRangePicker from "../../../../Components/ITopupDateRangePicker";
import Utils from "../../../../Helpers/Utils";
import { FilterGrid } from "../SalesReports";
import { useGetReportLogsMutation } from "../../../../Services/ReportsService";
import { SalesDataArray } from "../../../../Helpers/Interfaces/SalesReports";

interface Filters {
  dateRange: [Dayjs | null, Dayjs | null];
  reportType: string;
  status: string;
}

const ReportLogs = () => {
  const today = dayjs();
  const [filters, setFilters] = React.useState<Filters>({
    dateRange: [today, today],
    reportType: "",
    status: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [getReportLogsTrigger, { data, isLoading, error }] =
    useGetReportLogsMutation();

  useEffect(() => {
    const dynamicFilters = [];

    if (filters?.dateRange[0] && filters?.dateRange[1]) {
      dynamicFilters.push({
        key: "startDate",
        value: filters.dateRange[0]?.format("YYYY-MM-DD"),
      });
      dynamicFilters.push({
        key: "endDate",
        value: `${filters.dateRange[1]?.format("YYYY-MM-DD")} 23:59:59`,
      });
      if (filters.status !== "") {
        dynamicFilters.push({
          key: "status",
          value: filters.status,
        });
      }
    }
    const filterReq: any = {
      filters: [...dynamicFilters],
      page: page + 1,
      pageSize: rowsPerPage,
    };

    getReportLogsTrigger(filterReq);
  }, [page, rowsPerPage, filters.dateRange, filters.status]);

    const handleReset = () => {
    setFilters({
      ...filters,
      status: "",
      dateRange: [today, today],
    });
    setPage(0);
    setRowsPerPage(10);
  };


  const handleExport = async (reportLog: SalesDataArray) => {
    const file = reportLog?.responses?.[0];

    if (!file?.fileBase64 || !file?.fileName || !file?.mimeType) {
      return;
    }

    const byteCharacters = atob(file.fileBase64);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: file.mimeType });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof Filters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

    const isDateChanged = Utils.isDateChanged(filters.dateRange);



  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "5%",
            width: { lg: "45%", md: "100%", sm: "100%", xs: "100%" },
          }}
        >
          <Breadcrumb
            name="Report Logs"
            items={[{ label: "Reports" }, { label: "Report Logs" }]}
          />
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FilterGrid
          container
          spacing={2}
          alignItems="center"
          className="filter-grid"
        >
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }} sx={{ mt: 1 }}>
            <ITopupDateRangePicker
              handleFilterChange={handleFilterChange}
              height={42}
              value={filters.dateRange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid2>
          {filters?.reportType !== "" || filters?.status !== "" || isDateChanged ? (
            <div style={{ marginRight: 8, marginTop: 2 }}>
              <Button variant="outlined" color="warning" onClick={handleReset}>
                Reset
              </Button>
            </div>
          ) : (
            <div style={{ marginRight: 8, marginTop: 2 }} />
          )}
        </FilterGrid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Batch ID</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Updated By</TableCell>
              <TableCell>Updated At</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading &&
              [...Array(10)].map((_, index) => (
                <TableRow key={index}>
                  {[...Array(10)].map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton
                        variant="text"
                        width={colIndex === 0 ? 30 : "80%"}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!isLoading &&
              data &&
              data?.data &&
              data?.data.length > 0 &&
              data?.data.map((reportLog) => (
                <TableRow key={reportLog._id}>
                  <TableCell>{reportLog?.batchId}</TableCell>
                  <TableCell>{reportLog?.type}</TableCell>
                  <TableCell>{reportLog?.updatedBy}</TableCell>
                  <TableCell>
                    {dayjs(reportLog?.updatedAt).format("DD.MM.YYYY, HH:mm:ss")}
                  </TableCell>
                  <TableCell>{reportLog?.status}</TableCell>
                  <TableCell>
                    <Tooltip title="Export as Excel">
                      <IconButton
                        disabled={reportLog?.status === "pending"}
                        onClick={() => handleExport(reportLog)}
                        sx={{
                          color: "success.main",
                          backgroundColor: "success.lighter",
                          "&:hover": {
                            backgroundColor: "success.light",
                            color: "white",
                          },
                        }}
                      >
                        <FaRegFileExcel />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading && error && (error as any)?.data?.message && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {(error as any).data.message}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          showFirstButton
          showLastButton
          component="div"
          count={data && data?.totalRecords ? data?.totalRecords : 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 15, 25]}
        />
      </TableContainer>
    </Box>
  );
};

export default ReportLogs;
